import React, { useState } from 'react';
import { Calendar as CalendarIcon, Filter, X, LayoutGrid, Monitor } from 'lucide-react';
import { Button } from '../../ui/button';
import { Calendar } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '../../ui/command';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import { format, subDays } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Componente MultiSelect genérico para DashboardFilters
 */
const MultiSelectFilter = ({ label, icon: Icon, options = [], selected = [], onToggle, onClear }) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="w-full justify-start md:w-auto border-dashed min-w-[140px]">
          <Icon className="mr-2 h-4 w-4" />
          {label}
          {selected.length > 0 && (
            <>
              <div className="mx-2 h-4 w-[1px] bg-slate-200 dark:bg-slate-700" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                {selected.length}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selected.length > 1 ? (
                  <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                    {selected.length} selec.
                  </Badge>
                ) : (
                  options
                    .filter((opt) => selected.includes(opt.value))
                    .map((opt) => (
                      <Badge
                        key={opt.value}
                        variant="secondary"
                        className="rounded-sm px-1 font-normal max-w-[100px] truncate"
                      >
                        {opt.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder={`Buscar ${label.toLowerCase()}...`} />
          <CommandList>
            <CommandEmpty>No se encontraron resultados.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  onSelect={() => onToggle(option.value)}
                >
                  <div
                    className={cn(
                      "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                      selected.includes(option.value)
                        ? "bg-primary text-primary-foreground"
                        : "opacity-50 [&_svg]:invisible"
                    )}
                  >
                    <X className="h-3 w-3" />
                  </div>
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {selected.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={onClear}
                    className="justify-center text-center font-medium"
                  >
                    Limpiar filtros
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Filtros avanzados para Dashboard
 */
const DashboardFilters = ({ 
  onFilterChange, 
  initialFilters = {}, 
  products = [], 
  channels = [], 
  userAgents = [],
  routes = [],
  showClientFilter = false 
}) => {
  const [dateRange, setDateRange] = useState(initialFilters.dateRange || null);
  const [selectedProducts, setSelectedProducts] = useState(initialFilters.products || []);
  const [selectedChannels, setSelectedChannels] = useState(initialFilters.channels || []);
  const [selectedUserAgents, setSelectedUserAgents] = useState(initialFilters.userAgents || []);
  const [selectedRoutes, setSelectedRoutes] = useState(initialFilters.routes || []);
  const [onlyMyClients, setOnlyMyClients] = useState(initialFilters.onlyMyClients || false);

  const handleDateSelect = (range) => {
    setDateRange(range);
    emitChange({ dateRange: range });
  };

  const applyDatePreset = (days) => {
    const to = new Date();
    const from = subDays(to, days);
    const range = { from, to };
    setDateRange(range);
    emitChange({ dateRange: range });
  };

  const applyMonthPreset = () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const range = { from, to: now };
    setDateRange(range);
    emitChange({ dateRange: range });
  };

  const toggleFilter = (setter, current, value, key) => {
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setter(next);
    emitChange({ [key]: next });
  };

  const resetFilters = () => {
    setDateRange(null);
    setSelectedProducts([]);
    setSelectedChannels([]);
    setSelectedUserAgents([]);
    setSelectedRoutes([]);
    setOnlyMyClients(false);
    onFilterChange({});
  };

  const emitChange = (changes) => {
    onFilterChange({
      dateRange,
      products: selectedProducts,
      channels: selectedChannels,
      userAgents: selectedUserAgents,
      routes: selectedRoutes,
      onlyMyClients,
      ...changes
    });
  };

  const hasAnyFilter = dateRange || selectedProducts.length > 0 || selectedChannels.length > 0 || 
                      selectedUserAgents.length > 0 || selectedRoutes.length > 0 || onlyMyClients;

  return (
    <div className="flex flex-col gap-4 py-4 md:flex-row md:items-center md:justify-between border-b border-slate-100 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm px-2 rounded-lg mb-6">
      <div className="flex flex-1 flex-wrap gap-2 items-center">
        {/* Date Range Picker with Presets */}
        <div className="flex items-center gap-1">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "justify-start text-left font-normal w-[220px]",
                  !dateRange && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateRange?.from ? (
                  dateRange.to ? (
                    <span className="truncate">
                      {format(dateRange.from, "dd MMM", { locale: es })} - {format(dateRange.to, "dd MMM", { locale: es })}
                    </span>
                  ) : (
                    format(dateRange.from, "dd MMM, y", { locale: es })
                  )
                ) : (
                  <span>Fecha</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="flex">
                <div className="flex flex-col gap-1 p-2 border-r border-slate-100 bg-slate-50/50">
                  <Button variant="ghost" size="sm" onClick={() => applyDatePreset(7)} className="justify-start text-xs h-8">
                    7 días
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => applyDatePreset(14)} className="justify-start text-xs h-8">
                    14 días
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => applyDatePreset(30)} className="justify-start text-xs h-8">
                    30 días
                  </Button>
                  <Button variant="ghost" size="sm" onClick={applyMonthPreset} className="justify-start text-xs h-8">
                    Este mes
                  </Button>
                </div>
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={dateRange}
                  onSelect={handleDateSelect}
                  numberOfMonths={1}
                />
              </div>
            </PopoverContent>
          </Popover>
          
          <div className="hidden xl:flex gap-1">
             <Button variant="outline" size="sm" onClick={() => applyDatePreset(7)} className="text-[10px] px-1.5 h-7">7D</Button>
             <Button variant="outline" size="sm" onClick={() => applyDatePreset(30)} className="text-[10px] px-1.5 h-7">30D</Button>
             <Button variant="outline" size="sm" onClick={applyMonthPreset} className="text-[10px] px-1.5 h-7">Mes</Button>
          </div>
        </div>

        {/* Dynamic Multi-Selects */}
        <MultiSelectFilter 
          label="Planes" 
          icon={Filter} 
          options={products} 
          selected={selectedProducts} 
          onToggle={(val) => toggleFilter(setSelectedProducts, selectedProducts, val, 'products')}
          onClear={() => { setSelectedProducts([]); emitChange({ products: [] }); }}
        />

        {userAgents.length > 0 && (
          <MultiSelectFilter 
            label="Dispositivo" 
            icon={Monitor} 
            options={userAgents} 
            selected={selectedUserAgents} 
            onToggle={(val) => toggleFilter(setSelectedUserAgents, selectedUserAgents, val, 'userAgents')}
            onClear={() => { setSelectedUserAgents([]); emitChange({ userAgents: [] }); }}
          />
        )}

        {routes.length > 0 && (
          <MultiSelectFilter 
            label="Ruta" 
            icon={LayoutGrid} 
            options={routes} 
            selected={selectedRoutes} 
            onToggle={(val) => toggleFilter(setSelectedRoutes, selectedRoutes, val, 'routes')}
            onClear={() => { setSelectedRoutes([]); emitChange({ routes: [] }); }}
          />
        )}

        {/* Toggle My Clients (Optional) */}
        {showClientFilter && (
          <Button
            variant={onlyMyClients ? "secondary" : "ghost"}
            size="sm"
            onClick={() => {
              const next = !onlyMyClients;
              setOnlyMyClients(next);
              emitChange({ onlyMyClients: next });
            }}
            className="w-full md:w-auto"
          >
            {onlyMyClients ? "Solo mis clientes" : "Todos los clientes"}
          </Button>
        )}
      </div>

      {/* Reset Button */}
      {hasAnyFilter && (
        <Button
          variant="ghost"
          size="sm"
          onClick={resetFilters}
          className="h-8 px-2 text-[var(--yool-error)] hover:text-[var(--yool-error)] hover:bg-[color-mix(in_srgb,var(--yool-error)_8%,transparent)]"
        >
          Resetear
          <X className="ml-2 h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export default DashboardFilters;
